<template>
    <div ref="chartEl" class="stl-chart-el stl-chart-fill"></div>
</template>

<script>
import { stlChartTextStyle } from "../../utils/settlementFormat.js";
import {
    CHART_AXIS,
    seriesColors,
    lineSeriesStyle,
    buildHighlightMarkLine,
    buildXAxisLabelFormatter,
    baseChartOption,
} from "../../utils/chartTheme.js";
import * as echarts from "echarts";

export default {
    name: "MonthlyTrendChart",
    props: {
        labels: { type: Array, default: () => [] },
        series: { type: Array, default: () => [] },
        unit: { type: String, default: "원" },
        /** line | stacked-bar */
        chartType: { type: String, default: "line" },
        highlightLabel: { type: String, default: null },
    },
    data() {
        return { chart: null, ro: null };
    },
    watch: {
        labels: { handler: "render", deep: true },
        series: { handler: "render", deep: true },
        unit: "render",
        chartType: "render",
        highlightLabel: "render",
    },
    mounted() {
        this.render();
        window.addEventListener("resize", this.resize);
        if (typeof ResizeObserver !== "undefined" && this.$refs.chartEl) {
            this.ro = new ResizeObserver(() => this.resize());
            this.ro.observe(this.$refs.chartEl);
        }
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.resize);
        if (this.ro) this.ro.disconnect();
        if (this.chart) {
            this.chart.dispose();
            this.chart = null;
        }
    },
    methods: {
        highlightIndex() {
            if (!this.highlightLabel || !this.labels.length) return -1;
            const target = String(this.highlightLabel).trim();
            return this.labels.findIndex((l) => String(l) === target);
        },
        formatAxisValue(v) {
            if (this.unit === "원" && v >= 10000) return `${Math.round(v / 10000)}만`;
            return v;
        },
        formatTooltipValue(v) {
            if (this.unit !== "원") return `${Number(v).toLocaleString()} ${this.unit}`;
            if (v >= 10000) return `${Math.round(v / 10000).toLocaleString()}만원`;
            return `${Number(v).toLocaleString()}원`;
        },
        buildLineSeries(colors, hi) {
            const markLine = buildHighlightMarkLine(hi);
            return this.series.map((s, seriesIdx) => {
                const color = colors[seriesIdx % colors.length];
                const role = s.role || (seriesIdx === 0 ? "primary" : "secondary");
                const style = lineSeriesStyle(role, color);
                return {
                    name: s.name,
                    type: "line",
                    smooth: true,
                    showSymbol: this.labels.length <= 12,
                    ...style,
                    markLine: seriesIdx === 0 ? markLine : undefined,
                    data: s.data || [],
                };
            });
        },
        buildStackedBarSeries(colors, hi) {
            const markLine = buildHighlightMarkLine(hi);
            const n = this.series.length;
            return this.series.map((s, seriesIdx) => ({
                name: s.name,
                type: "bar",
                stack: "billing",
                barMaxWidth: 40,
                emphasis: { focus: "series", itemStyle: { opacity: 0.92 } },
                markLine: seriesIdx === 0 ? markLine : undefined,
                itemStyle: {
                    color: s.color || colors[seriesIdx % colors.length],
                    borderRadius: seriesIdx === n - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0],
                },
                data: (s.data || []).map((v) => Number(v || 0)),
            }));
        },
        buildSeries(colors, hi) {
            if (this.chartType === "stacked-bar") return this.buildStackedBarSeries(colors, hi);
            return this.buildLineSeries(colors, hi);
        },
        render() {
            if (!this.$refs.chartEl || !this.labels.length) {
                if (this.chart) this.chart.clear();
                return;
            }
            if (!this.chart) this.chart = echarts.init(this.$refs.chartEl);
            const ts = (extra) => stlChartTextStyle(extra);
            const hi = this.highlightIndex();
            const colors = seriesColors(this.series.length);
            const isStackedBar = this.chartType === "stacked-bar";

            const base = baseChartOption(ts);

            const tooltip = {
                trigger: "axis",
                backgroundColor: "rgba(255,255,255,0.96)",
                borderColor: CHART_AXIS.axisLine,
                textStyle: ts({ fontSize: 12, color: CHART_AXIS.label }),
                formatter: isStackedBar
                    ? (params) => {
                          const lines = (params || []).map(
                              (p) => `${p.marker}${p.seriesName}: ${this.formatTooltipValue(p.value)}`,
                          );
                          const sum = (params || []).reduce((s, p) => s + Number(p.value || 0), 0);
                          lines.push(`<strong>합계: ${this.formatTooltipValue(sum)}</strong>`);
                          return `${params[0]?.axisValue || ""}<br/>${lines.join("<br/>")}`;
                      }
                    : undefined,
            };

            this.chart.setOption(
                {
                    ...base,
                    color: colors,
                    grid: { ...CHART_AXIS.grid },
                    tooltip,
                    xAxis: {
                        type: "category",
                        data: this.labels,
                        ...base.xAxis,
                        axisLabel: buildXAxisLabelFormatter(hi, () => ts()),
                    },
                    yAxis: {
                        type: "value",
                        name: this.unit,
                        ...base.yAxis,
                        axisLabel: {
                            ...base.yAxis.axisLabel,
                            formatter: (v) => this.formatAxisValue(v),
                        },
                    },
                    series: this.buildSeries(colors, hi),
                },
                true,
            );
            this.$nextTick(() => this.resize());
        },
        resize() {
            if (this.chart) this.chart.resize();
        },
    },
};
</script>

<style scoped>
.stl-chart-el {
    width: 100%;
    height: 100%;
    min-height: 160px;
}
</style>
